// iter-30 Phase 2 — The Staple Module.
// A reusable Model + Output-Length + Power-Mode control that travels wherever
// an LLM-driven module lives. Used by:
//
//   • SuperAdmin → LLM Routing tab           (scope="platform", per-surface)
//   • Creator Dashboard → Operations         (scope="creator", per-surface)
//   • SiloManage → Settings → LLM Routing    (scope="silo", per-surface)
//   • Workbench / Refinery / Tone / Probe    (scope="inline" — coming soon)
//   • Bump Studio drawers when a module gets dropped in (carries its scope)
//
// Save modes:
//   • Pin to silo      — silos.llm_routing_overrides[surface] (only when scope="silo")
//   • Pin to account   — users.llm_routing_overrides[surface]
//   • Just this run    — ephemeral session-only override (Phase 2 — wired with Boost)
//
// Power Mode dropdown sets model + output_length together with one click.
// Custom user presets render alongside the 6 system defaults.

import { useEffect, useMemo, useState, useRef } from "react";
import { FreeNumberInput } from "@/components/ui/free-number-input";
import { toast } from "sonner";
import { Lightning, FloppyDisk, ArrowsCounterClockwise, Plus, X, Timer } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import InfoTip from "@/components/InfoTip";
import { MODEL_GLOSSARY, modelTooltipBody } from "@/lib/modelGlossary";

const SAVE_MODES = [
  { id: "silo", label: "Pin to this silo", help: "Updates silos.llm_routing_overrides — survives reload." },
  { id: "creator", label: "Pin to my account", help: "Updates my account's overrides — applies to every silo I own that doesn't override." },
  { id: "ephemeral", label: "Just this run", help: "One-shot ephemeral override — auto-reverts after the next call to this surface (Boost-powered)." },
];

export default function ModelOutputControl({
  surface,            // {id, label, default_model, default_provider, ...}
  scope,              // "platform" | "creator" | "silo"
  siloId,             // required when scope === "silo"
  resolved,           // {model, provider, source} — current effective value
  onSaved,            // callback after a successful save
  registry,           // /api/models/registry response (provider list + pricing)
  compact = false,    // show a denser inline variant (used in run-button areas)
}) {
  const [presets, setPresets] = useState({ system: [], custom: [] });
  const [pendingModel, setPendingModel] = useState(null);
  const [pendingProvider, setPendingProvider] = useState(null);
  const [pendingTokens, setPendingTokens] = useState(null);
  const [saveMode, setSaveMode] = useState(scope === "silo" ? "silo" : "creator");
  const [busy, setBusy] = useState(false);
  const [savingPreset, setSavingPreset] = useState(false);
  const [newPresetName, setNewPresetName] = useState("");
  // Boost state
  const [activeBoost, setActiveBoost] = useState(null);
  const [boostModalOpen, setBoostModalOpen] = useState(false);
  const [boostDuration, setBoostDuration] = useState(15);
  const [boostCostCap, setBoostCostCap] = useState(800);
  const [tick, setTick] = useState(0);  // re-render every sec for countdown
  const tickRef = useRef(null);

  useEffect(() => { (async () => {
    try { const r = await api.get("/me/power-mode-presets"); setPresets(r.data); }
    catch { /* non-blocking */ }
  })(); }, []);

  // Poll active boost for THIS surface every 30s + tick countdown every sec.
  const reloadBoost = async () => {
    try {
      const r = await api.get("/boost/active");
      const mine = (r.data?.sessions || []).find((s) => s.surface === surface.id);
      setActiveBoost(mine || null);
    } catch { /* non-blocking */ }
  };
  useEffect(() => {
    reloadBoost();
    const int = setInterval(reloadBoost, 30000);
    return () => clearInterval(int);
  }, [surface.id]); // eslint-disable-line

  useEffect(() => {
    if (!activeBoost) return;
    tickRef.current = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(tickRef.current);
  }, [activeBoost?.id]); // eslint-disable-line

  const allChoices = useMemo(() => {
    if (!registry?.providers) return [];
    const list = [];
    for (const p of registry.providers) {
      const pricing = p.pricing_per_1m || {};
      for (const m of (p.models || [])) {
        const px = pricing[m] || {};
        list.push({ provider: p.provider, model: m, label: `${p.label} · ${m}`, inPrice: px.in, outPrice: px.out });
      }
    }
    return list;
  }, [registry]);

  const maxCapForModel = (model) => {
    if (!registry?.providers) return 8192;
    for (const p of registry.providers) {
      const cap = (p.max_output_cap || {})[model];
      if (cap) return cap;
    }
    return 8192;
  };

  const currentModel = pendingModel || resolved?.model || surface.default_model;
  const currentProvider = pendingProvider || resolved?.provider || surface.default_provider;
  const currentTokens = pendingTokens ?? 4096; // platform-level doesn't track tokens yet
  const isDirty = !!pendingModel || pendingTokens !== null;

  const priceFor = (model) => allChoices.find((c) => c.model === model);
  const px = priceFor(currentModel);

  const applyPreset = (preset) => {
    setPendingModel(preset.model);
    setPendingProvider(preset.provider);
    setPendingTokens(preset.max_output_tokens);
    toast.success(`Applied "${preset.name}" — click Save to persist.`);
  };

  const onPickModel = (value) => {
    if (!value) return;
    const [provider, model] = value.split("|");
    setPendingModel(model);
    setPendingProvider(provider);
  };

  const save = async () => {
    if (!isDirty) return;
    setBusy(true);
    try {
      if (saveMode === "ephemeral") {
        // "Just this run" — start a one-shot boost on this surface that
        // auto-cancels after the next call. Re-uses the boost machinery so
        // we have a single ephemeral-override pathway.
        await api.post("/boost/start", {
          surface: surface.id,
          target_model: currentModel,
          target_provider: currentProvider,
          target_max_tokens: currentTokens,
          duration_min: 60,            // generous outer bound; just_this_run is what really ends it
          cost_cap_cents: boostCostCap,
          just_this_run: true,
        });
        toast.success(`Just-this-run armed · ${currentModel}. Auto-reverts after the next call.`);
      } else {
        const body = { model: currentModel, provider: currentProvider };
        if (saveMode === "creator") {
          await api.patch(`/me/llm-routing/${surface.id}`, body);
        } else if (saveMode === "silo" && siloId) {
          await api.patch(`/silos/${siloId}/llm-routing/${surface.id}`, body);
        }
        toast.success(`Saved · ${surface.label} → ${currentModel}`);
      }
      setPendingModel(null);
      setPendingProvider(null);
      setPendingTokens(null);
      onSaved?.();
      reloadBoost();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const startBoost = async () => {
    setBusy(true);
    try {
      const r = await api.post("/boost/start", {
        surface: surface.id,
        target_model: currentModel,
        target_provider: currentProvider,
        target_max_tokens: currentTokens,
        duration_min: boostDuration,
        cost_cap_cents: boostCostCap,
      });
      setActiveBoost(r.data);
      setBoostModalOpen(false);
      toast.success(`Boost active · ${currentModel} for ${boostDuration} min`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Boost failed");
    } finally { setBusy(false); }
  };

  const cancelBoost = async () => {
    if (!activeBoost) return;
    setBusy(true);
    try {
      await api.post(`/boost/${activeBoost.id}/cancel`);
      setActiveBoost(null);
      toast.success("Boost cancelled.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    } finally { setBusy(false); }
  };

  const extendBoost = async () => {
    if (!activeBoost) return;
    setBusy(true);
    try {
      const r = await api.post(`/boost/${activeBoost.id}/extend?minutes=15`);
      setActiveBoost(r.data);
      toast.success("Boost extended +15 min.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Extend failed");
    } finally { setBusy(false); }
  };

  // Time-remaining helpers (re-renders via tick).
  const timeRemaining = () => {
    if (!activeBoost?.expires_at) return null;
    const ms = new Date(activeBoost.expires_at).getTime() - Date.now();
    if (ms <= 0) return null;
    const m = Math.floor(ms / 60000);
    const s = Math.floor((ms % 60000) / 1000);
    return `${m}:${s.toString().padStart(2, "0")}`;
  };
  const timeLeft = timeRemaining(); // eslint-disable-line no-unused-vars
  // Auto-clear stale boost on local tick when expires.
  useEffect(() => {
    if (!activeBoost?.expires_at) return;
    if (new Date(activeBoost.expires_at).getTime() <= Date.now()) {
      setActiveBoost(null);
      toast.info(`Boost expired on ${surface.label}.`);
    }
  }, [tick, activeBoost?.expires_at]); // eslint-disable-line

  const clearOverride = async () => {
    setBusy(true);
    try {
      if (saveMode === "creator") {
        await api.patch(`/me/llm-routing/${surface.id}`, { model: null, provider: null });
      } else if (saveMode === "silo" && siloId) {
        await api.patch(`/silos/${siloId}/llm-routing/${surface.id}`, { model: null, provider: null });
      }
      setPendingModel(null);
      setPendingProvider(null);
      setPendingTokens(null);
      toast.success(`Cleared ${surface.label} override at ${saveMode} layer`);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Clear failed");
    } finally { setBusy(false); }
  };

  const saveAsPreset = async () => {
    if (!newPresetName.trim()) {
      toast.error("Name your preset first.");
      return;
    }
    setSavingPreset(true);
    try {
      const r = await api.post("/me/power-mode-presets", {
        name: newPresetName.trim(),
        model: currentModel,
        provider: currentProvider,
        max_output_tokens: currentTokens,
      });
      setPresets(r.data);
      setNewPresetName("");
      toast.success(`Saved "${newPresetName.trim()}" as a custom preset.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save preset");
    } finally { setSavingPreset(false); }
  };

  const cap = maxCapForModel(currentModel);
  const tooltipBody = modelTooltipBody(currentModel);

  return (
    <div data-testid={`model-output-control-${surface.id}`} className={`bg-white border hairline rounded-lg ${compact ? "p-3" : "p-4"} ${isDirty ? "border-[#C16E5A] bg-[#FFF7F1]" : ""}`}>
      {/* Power Mode quick-pick row */}
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] flex items-center gap-1">
          <Lightning size={12} weight="duotone" /> Power Mode
        </span>
        <select
          data-testid={`power-mode-${surface.id}`}
          value=""
          onChange={(e) => {
            if (!e.target.value) return;
            const all = [...presets.system, ...presets.custom];
            const p = all.find((x) => x.id === e.target.value);
            if (p) applyPreset(p);
            e.target.value = "";
          }}
          className="text-xs px-2 py-1.5 border hairline rounded-md bg-white"
        >
          <option value="">Apply preset…</option>
          <optgroup label="System defaults">
            {presets.system.map((p) => (
              <option key={p.id} value={p.id}>{p.tier_emoji} {p.name} — {p.tagline}</option>
            ))}
          </optgroup>
          {presets.custom.length > 0 && (
            <optgroup label="My presets">
              {presets.custom.map((p) => (
                <option key={p.id} value={p.id}>★ {p.name}{p.tagline ? ` — ${p.tagline}` : ""}</option>
              ))}
            </optgroup>
          )}
        </select>
        <InfoTip placement="top-center" width={340} testid={`power-mode-tip-${surface.id}`}>
          <strong>Power Mode presets</strong> are named (model, output-length) recipes.
          Pick one to dial both controls at once.
          <br /><br />
          Save your own combos via the <em>Save as preset</em> button — they appear here for every surface.
        </InfoTip>
      </div>

      {/* Model + output length */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1 flex items-center gap-1">
            Model
            {tooltipBody && (
              <InfoTip
                icon="eye" iconSize={14}
                label={`About ${tooltipBody.title}`}
                placement="top-left" width={420}
                testid={`model-tip-${surface.id}`}
              >
                <div>
                  <div className="font-medium text-[#FDFCF8] text-xs mb-1">{tooltipBody.title}</div>
                  <div className="text-[#FDFCF8]/80 mb-3 italic">{tooltipBody.blurb}</div>
                  {tooltipBody.sections.map((sec, i) => (
                    <div key={i} className="mb-2">
                      <div className="text-[9px] uppercase tracking-[0.2em] text-[#FDFCF8]/60 mb-0.5">{sec.label}</div>
                      <div className="text-[11px] text-[#FDFCF8]/95 leading-relaxed">{sec.text}</div>
                    </div>
                  ))}
                </div>
              </InfoTip>
            )}
          </div>
          <select
            data-testid={`staple-model-select-${surface.id}`}
            value={`${currentProvider}|${currentModel}`}
            onChange={(e) => onPickModel(e.target.value)}
            disabled={busy}
            className="w-full text-xs px-2 py-2 border hairline rounded-md bg-white"
          >
            {allChoices.map((c) => (
              <option key={`${c.provider}|${c.model}`} value={`${c.provider}|${c.model}`}>{c.label}</option>
            ))}
          </select>
          {px && (
            <div className="text-[10px] text-[#6B6A68] mt-1 font-mono tabular-nums" data-testid={`staple-cost-${surface.id}`}>
              ≈ ${px.inPrice?.toFixed(2)} in / ${px.outPrice?.toFixed(2)} out per 1M tokens
            </div>
          )}
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1 flex items-center gap-1">
            Output length
            <InfoTip placement="top-center" width={300} testid={`output-tip-${surface.id}`}>
              <strong>Output length</strong> caps how many tokens the model returns per call.
              <br /><br />
              Lower = faster + cheaper but truncated answers.
              Higher = full answers but more cost (output tokens are typically 4-10× pricier than input).
              <br /><br />
              Most surfaces work great at <strong>4096</strong>; only Tone Architect and Reasoning-Heavy presets benefit from the full <strong>8192</strong>+.
            </InfoTip>
          </div>
          <input
            type="range"
            data-testid={`staple-output-slider-${surface.id}`}
            min={128} max={cap} step={128}
            value={currentTokens}
            onChange={(e) => setPendingTokens(parseInt(e.target.value, 10))}
            disabled={busy}
            className="w-full"
          />
          <div className="text-[10px] text-[#6B6A68] mt-0.5 tabular-nums flex justify-between">
            <span>128</span>
            <span className="font-medium text-[#1C1B1A]">{currentTokens.toLocaleString()} / {cap.toLocaleString()} tokens</span>
            <span>{cap.toLocaleString()}</span>
          </div>
        </div>
      </div>

      {/* Save mode + actions */}
      <div className="flex items-center gap-2 flex-wrap pt-2 border-t hairline">
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Save as</span>
        <div className="flex items-center gap-1 flex-wrap">
          {SAVE_MODES.filter((m) => scope === "silo" ? true : m.id !== "silo").map((m) => (
            <label key={m.id} className={`text-[11px] px-2 py-1 rounded-md cursor-pointer flex items-center gap-1 ${saveMode === m.id ? "bg-[#1C1B1A] text-[#FDFCF8]" : "bg-[#F4F2EC] text-[#6B6A68] hover:text-[#1C1B1A]"}`} title={m.help}>
              <input type="radio" className="hidden" checked={saveMode === m.id} onChange={() => setSaveMode(m.id)}
                data-testid={`staple-save-mode-${surface.id}-${m.id}`} />
              {m.label}
            </label>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          {/* Boost ⚡ button — inline with the staple, travels with it */}
          {activeBoost ? (
            <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-[#FFF7F1] border border-[#C16E5A] text-[#C16E5A]"
              data-testid={`boost-active-pill-${surface.id}`}>
              <Lightning size={12} weight="fill" className="animate-pulse" />
              <span className="text-[11px] font-mono tabular-nums">
                {timeLeft || "expiring…"}
              </span>
              <button onClick={extendBoost} disabled={busy}
                data-testid={`boost-extend-${surface.id}`}
                className="text-[10px] px-1 py-0.5 rounded hover:bg-[#FCEDE6] ml-1"
                title="Extend +15 min">
                <Timer size={11} />
              </button>
              <button onClick={cancelBoost} disabled={busy}
                data-testid={`boost-cancel-${surface.id}`}
                className="text-[10px] px-1 py-0.5 rounded hover:bg-[#FCEDE6]"
                title="Cancel boost">
                <X size={11} />
              </button>
            </div>
          ) : (
            <button onClick={() => setBoostModalOpen(true)}
              disabled={busy}
              data-testid={`boost-button-${surface.id}`}
              className="text-[11px] px-2 py-1.5 rounded-md border hairline text-[#C16E5A] hover:bg-[#FFF7F1] hover:border-[#C16E5A] flex items-center gap-1"
              title="Boost — temporarily upgrade this surface with cost cap">
              <Lightning size={12} weight="duotone" /> Boost
            </button>
          )}
          <button
            onClick={clearOverride}
            disabled={busy || resolved?.source === "platform"}
            data-testid={`staple-clear-${surface.id}`}
            className="text-[11px] px-2 py-1.5 rounded-md border hairline text-[#6B6A68] hover:text-[#C16E5A] hover:border-[#C16E5A] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <ArrowsCounterClockwise size={12} /> Inherit
          </button>
          <button
            onClick={save}
            disabled={busy || !isDirty}
            data-testid={`staple-save-${surface.id}`}
            className="text-[11px] px-3 py-1.5 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <FloppyDisk size={12} /> {saveMode === "ephemeral" ? "Arm one-shot" : "Save"}
          </button>
        </div>
      </div>

      {/* Boost confirm modal */}
      {boostModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setBoostModalOpen(false)} data-testid={`boost-modal-${surface.id}`}>
          <div className="modal-safe bg-white rounded-lg shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-3">
              <Lightning size={20} weight="duotone" className="text-[#C16E5A]" />
              <h3 className="font-serif-display text-xl">Boost · {surface.label}</h3>
            </div>
            <p className="text-sm text-[#6B6A68] mb-4 leading-relaxed">
              Temporarily route this surface to <strong className="text-[#1C1B1A] font-mono">{currentModel}</strong> with up to <strong>{currentTokens.toLocaleString()}</strong> output tokens.
              Auto-reverts when the timer expires <em>or</em> the cost cap is hit, whichever fires first.
            </p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Duration (min)</label>
                <FreeNumberInput min={1} max={240} value={boostDuration}
                  onChange={(v) => setBoostDuration(v)}
                  data-testid={`boost-duration-${surface.id}`}
                  className="w-full mt-1 px-2 py-2 border hairline rounded-md text-sm" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Cost cap ($)</label>
                <FreeNumberInput min={0.1} max={50} step={0.5} precision={2}
                  value={boostCostCap / 100}
                  onChange={(v) => setBoostCostCap(Math.round(v * 100))}
                  data-testid={`boost-cost-cap-${surface.id}`}
                  className="w-full mt-1 px-2 py-2 border hairline rounded-md text-sm" />
              </div>
            </div>
            <div className="flex items-center gap-2 justify-end">
              <button onClick={() => setBoostModalOpen(false)} disabled={busy}
                data-testid={`boost-modal-cancel-${surface.id}`}
                className="text-sm px-3 py-2 text-[#6B6A68] hover:text-[#1C1B1A]">
                Cancel
              </button>
              <button onClick={startBoost} disabled={busy}
                data-testid={`boost-modal-confirm-${surface.id}`}
                className="text-sm px-4 py-2 rounded-md bg-[#C16E5A] text-[#FDFCF8] hover:bg-[#A85E4A] flex items-center gap-1">
                <Lightning size={14} weight="duotone" /> Boost {boostDuration} min · ${(boostCostCap/100).toFixed(2)} cap
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Save-as-preset row (only when dirty so we don't clutter at rest) */}
      {isDirty && (
        <div className="mt-3 pt-3 border-t hairline flex items-center gap-2 flex-wrap" data-testid={`staple-save-preset-row-${surface.id}`}>
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68]">Save current as preset</span>
          <input
            type="text"
            placeholder="My preset name…"
            value={newPresetName}
            onChange={(e) => setNewPresetName(e.target.value)}
            disabled={savingPreset}
            data-testid={`staple-preset-name-${surface.id}`}
            className="flex-1 min-w-[180px] text-xs px-2 py-1.5 border hairline rounded-md bg-white"
          />
          <button
            onClick={saveAsPreset}
            disabled={savingPreset || !newPresetName.trim()}
            data-testid={`staple-preset-create-${surface.id}`}
            className="text-[11px] px-2 py-1.5 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-40 flex items-center gap-1"
          >
            <Plus size={12} /> Save preset
          </button>
        </div>
      )}
    </div>
  );
}
